<template>
  <v-card>
    <slot name="header" />
    <info-box
      v-if="statusesError || statisticsError"
      :info-message="t('dashboard.details.gained-contacts-details.error-loading')"
      type="error"
    />
    <info-box
      v-else-if="!statusesLoading && !statisticsLoading && statusesStatistics?.length === 0"
      :info-message="t('dashboard.details.no-data')"
    />
    <div v-else>
      <Loader v-if="statusesLoading || statisticsLoading" />
      <v-card-text
        v-else
        class="mt-1 mb-0 pb-0"
      >
        <v-table
          striped="even"
          class="custom-striped-table"
        >
          <thead>
            <tr class="tr-header">
              <th class="text-left" />
              <th
                class="text-left py-2"
                v-for="status in pipelineStatuses"
                :key="status.value"
              >
                <span class="th-title">{{ status.text }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in statusesStatistics"
              style="cursor: pointer"
              @click="updateStatusesStatistics(item.companyId, index)"
              :key="item.companyName"
            >
              <td class="td-company-name py-2">
                {{ item.companyName }}
              </td>
              <td
                v-for="value in item.statuses"
                :key="value.status"
              >
                <!--              {{ value?.status }} - -->
                <span
                  class="td-status-count"
                  v-if="value?.count"
                >
                  {{ value?.count }}
                </span>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </div>
    <template v-if="singleCompanySelected">
      <v-card-actions>
        <v-btn
          variant="text"
          color="grey-darken-3"
          @click="refetchData"
        >
          {{ t('labels.back') }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="text"
          color="grey-darken-3"
          @click="emit('close-details')"
        >
          {{ t('labels.cancel') }}
        </v-btn>
      </v-card-actions>
    </template>
    <slot
      v-else
      name="footer"
    />
  </v-card>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { AuthInstance } from '@/auth'
import InfoBox from '@/components/InfoBox.vue'
import statusForSelect, { defaultSystemStatuses } from '@/lib/leadStatus'
import Loader from '@/modules/dashboard/dashboardDetails/shared/loader.vue'
import GET_LEADS_PIPELINE_STATUSES from '@/modules/leads/LeadList/Cards/queries/GetLeadsPipelineStatuses.gql'
import LEADS_STATUSES_STATISTICS from '@/modules/leads/queries/leadsStatusesStatistics.gql'

import {
  DashboardContext,
  dashboardContextDefault,
  DashboardContextSymbol,
} from '../dashboardContext'

const route = useRoute()
const { t } = useI18n()
const $auth = inject<AuthInstance>('auth')
const singleCompanySelected = ref(false)
const isMasterView = computed(() => route.path.startsWith('/master'))
const routeParamId = computed(() => route?.params?.id || null)
const hasSelectedChildCompany = computed(() => !!routeParamId.value)
const emit = defineEmits(['close-details'])

const dashboardContext = inject<DashboardContext>(DashboardContextSymbol, dashboardContextDefault)

const variables = computed(() => ({
  input: {
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value ?? null,
    ...dashboardContext.formattedDateFilters.value,
  },
}))
const {
  result: statusesResult,
  loading: statusesLoading,
  error: statusesError,
  refetch: refetchStatuses,
} = useQuery(GET_LEADS_PIPELINE_STATUSES, variables)

type PipelineStatus = {
  name: string
  position: number
}

const pipelineStatuses = computed(() => {
  const statuses: PipelineStatus[] = statusesResult.value?.leadsPipelineStatuses || []
  const uniqueStatuses = Array.from(
    new Map(statuses.map((status) => [status.name, status])).values()
  )
  return uniqueStatuses.map((status) => ({
    value: status.name,
    position: status.position,
    text: defaultSystemStatuses.includes(status.name)
      ? t(statusForSelect[status.name])
      : status.name,
  }))
})

const {
  result: statisticsResult,
  loading: statisticsLoading,
  error: statisticsError,
  refetch: refetchStatistics,
} = useQuery(LEADS_STATUSES_STATISTICS, variables)

const allCompaniesStatusesStatistics = (statusesStatistics) => {
  if (!statusesStatistics || statusesStatistics.length === 0) {
    return []
  }
  let statuses = []
  statusesStatistics.forEach((statistics) => {
    if (statuses.length === 0) {
      statuses = statistics.statuses.map((status) => {
        return {
          status: status.status,
          count: status.count,
          position: status.position,
        }
      })
    } else {
      statistics?.statuses.forEach((status, index) => {
        if (statuses[index] && status.status) {
          if (statuses[index].count) {
            statuses[index].count = statuses[index].count + status.count
          } else {
            statuses[index].count = status.count
            statuses[index].status = status.status
          }
        }
      })
    }
  })
  return {
    companyName: t('dashboard.details.impressions.allCompanies'),
    statuses: statuses,
  }
}

const statusesStatistics = computed(() => {
  const leadsStatisticsResult = statisticsResult.value?.getLeadsStatusesStatistics || []
  if (!leadsStatisticsResult || leadsStatisticsResult.length === 0) {
    return []
  }

  const mappedResult = leadsStatisticsResult.map((statistics) => {
    return {
      ...statistics,
      statuses: pipelineStatuses.value.map((pipelineStatus) => {
        const statisticsStatus = statistics.statuses.find(
          (statistic) =>
            statistic.status.trim().toLowerCase() === pipelineStatus.value.trim().toLowerCase()
        )

        return {
          ...statisticsStatus,
          position: pipelineStatus.position,
        }
      }),
    }
  })

  if (hasSelectedChildCompany.value || !isMasterView.value || singleCompanySelected.value) {
    return mappedResult
  }

  mappedResult.unshift(allCompaniesStatusesStatistics(mappedResult))

  return mappedResult
})

const refetchData = () => {
  singleCompanySelected.value = false
  refetchStatuses({
    input: {
      isMasterView: isMasterView.value,
      childCompanyId: routeParamId.value,
      ...dashboardContext.formattedDateFilters.value,
    },
  })

  refetchStatistics({
    input: {
      isMasterView: isMasterView.value,
      childCompanyId: routeParamId.value,
      ...dashboardContext.formattedDateFilters.value,
    },
  })
}

const updateStatusesStatistics = (companyId, index) => {
  if (!isMasterView.value || index === 0 || singleCompanySelected.value) {
    return
  }

  singleCompanySelected.value = true
  refetchStatuses({
    input: {
      isMasterView: $auth.user.companyId !== companyId,
      childCompanyId: $auth.user.companyId === companyId ? null : companyId,
      ...dashboardContext.formattedDateFilters.value,
    },
  })

  refetchStatistics({
    input: {
      isMasterView: $auth.user.companyId !== companyId,
      childCompanyId: $auth.user.companyId === companyId ? null : companyId,
      ...dashboardContext.formattedDateFilters.value,
    },
  })
}

onUnmounted(() => {
  singleCompanySelected.value = false
})
</script>
<style scoped>
.custom-striped-table tbody tr td,
.custom-striped-table thead th {
  border-top: none !important;
  border-bottom: none !important;
}
.custom-striped-table tbody tr:nth-child(even) {
  background-color: #eef4f4;
}
.custom-striped-table tbody tr td:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}
.custom-striped-table tbody tr td:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}
.th-title {
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
}
.td-company-name {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}
.td-status-count {
  color: #175e51;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 26px;
}
.tr-header {
  background-color: #eef4f4;
}
</style>
